import React from "react";
import { PressableCard } from "@/ui";
import FireAnimation from "./FireAnimation";

interface StreakOption {
  days: number;
  label: string;
  multiplier?: number;
}

const STREAK_OPTIONS: StreakOption[] = [
  { days: 7, label: "Good" },
  { days: 14, label: "Great", multiplier: 2 },
  { days: 30, label: "Incredible", multiplier: 3 },
  { days: 50, label: "Unstoppable", multiplier: 5 },
];

const DEFAULT_MESSAGE =
  "Streak goals help you stay committed and build a habit.";

interface StreakCommitmentProps {
  onStreakGoalChange?: (days: number) => void;
  initialDays?: number | null;
  selectedDays: number | null;
}

/**
 * Placement streak-goal commitment picker (iOS StreakScreen). Replaces the
 * passive "day 1" calendar. The parent (PlacementTestPage) owns the selected
 * value + the continue button (which is disabled until a goal is chosen).
 */
const StreakCommitment: React.FC<StreakCommitmentProps> = ({
  onStreakGoalChange,
  selectedDays,
}) => {
  const handleSelect = (days: number) => {
    onStreakGoalChange?.(days);
  };

  const selectedOption = STREAK_OPTIONS.find((o) => o.days === selectedDays);

  return (
    <div className="flex flex-col items-center justify-center w-full h-full gap-6 px-5">
      <FireAnimation />

      <div className="text-2xl font-bold text-[#111827] text-center leading-9">
        Commit to learning!
      </div>

      <div className="flex flex-col gap-3 w-full max-w-[420px]">
        {STREAK_OPTIONS.map((option) => (
          <PressableCard
            key={option.days}
            selected={selectedDays === option.days}
            onPress={() => handleSelect(option.days)}
            padding={16}
          >
            <div className="flex flex-row justify-between items-center w-full min-h-[42px]">
              <span className="text-lg font-bold text-[#111827]">
                {option.days} days
              </span>
              <span className="text-base text-[var(--color-grey-other)]">
                {option.label}
              </span>
            </div>
          </PressableCard>
        ))}
      </div>

      <div className="text-base text-[var(--color-grey)] text-center leading-6 px-5">
        {selectedOption?.multiplier ? (
          <>
            You&apos;ll be{" "}
            <span className="font-bold">{selectedOption.multiplier}x more</span>{" "}
            likely to complete the course!
          </>
        ) : (
          DEFAULT_MESSAGE
        )}
      </div>
    </div>
  );
};

export default StreakCommitment;
